"use client";
import { useEffect } from "react";
import { useParams } from "next/navigation";

import ForwardIcon from "@/assets/icons/forward-15.svg";
import PauseIcon from "@/assets/icons/pause.svg";
import PlayIcon from "@/assets/icons/play.svg";
import BackwardIcon from "@/assets/icons/replay-5.svg";
import { AudioSlider } from "@/components/partials";
import { AudioInfoType, useAudioContext, useAuth } from "@/context";
import { useFetch } from "@/hooks/useFetch";
import { VALIDATE_K } from "@/lib/constants";
import { LectureType } from "@/lib/schema";
import { apiAudio } from "@/lib/utils";

export function AudioSection({ id, src, thumbnail, title }: AudioInfoType) {
  const [fetchData, { loading }] = useFetch<{ data: LectureType }>();
  const { isAuth } = useAuth();
  const params = useParams();
  const {
    isPlaying,
    currentTime,
    duration,
    togglePlayPause,
    handleForward,
    handleBackward,
    setAudio,
    formatTime,
    toggleAudioDrawer,
  } = useAudioContext();

  useEffect(() => {
    if (isAuth) {
      fetchData(`/lecture/${params.track}`, {
        cache: "no-store",
        memoryCache: true,
        next: { tags: [VALIDATE_K.lectures] },
      })
        .then((c) =>
          setAudio(
            {
              id,
              src: apiAudio(c.data?.data.audio),
              thumbnail,
              title,
            },
            false
          )
        )
        .catch(() =>
          setAudio(
            {
              id,
              src: apiAudio(src),
              thumbnail,
              title,
            },
            false
          )
        );
    } else {
      setAudio(
        {
          id,
          src: apiAudio(src),
          thumbnail,
          title,
        },
        false
      );
    }

    return () => toggleAudioDrawer(true);
  }, [isAuth, params]);

  const loadingNewSrc = loading && isAuth;

  return (
    <>
      <AudioSlider size="lg" disabled={loadingNewSrc} />
      <div className="flex justify-between text-sm text-[#898480] mt-1 mb-8">
        <span>{formatTime(currentTime)}</span>
        <span>
          {currentTime >= duration ? "" : "-"}
          {formatTime((duration || 0) - currentTime)}
        </span>
      </div>

      <div className="flex items-center gap-6 justify-center mb-8">
        <button
          disabled={loadingNewSrc}
          type="button"
          className="transition active:-rotate-45 active:scale-95"
          onClick={handleBackward}
        >
          <BackwardIcon style={{ width: 32, height: 32, color: "#84807D" }} />
        </button>
        <button
          type="button"
          className="transition active:scale-75 w-16 h-16 bg-primary flex items-center justify-center rounded-full"
          disabled={loadingNewSrc}
          onClick={togglePlayPause}
        >
          {isPlaying ? (
            <PauseIcon style={{ width: 22, height: 22, color: "#fff" }} />
          ) : (
            <PlayIcon style={{ width: 18, height: 18, color: "#fff" }} />
          )}
        </button>
        <button
          type="button"
          className="transition active:rotate-45 active:scale-95"
          onClick={handleForward}
          disabled={loadingNewSrc}
        >
          <ForwardIcon style={{ width: 32, height: 32, color: "#84807D" }} />
        </button>
      </div>
    </>
  );
}
